import React, { useState, useEffect } from "react";
import PopUpModal from "./PopUpModal";
import api from "../utils/api";

const AddOneCategory = ({ 
  userId, 
  refreshCategories, 
  onSuccess,
  existingData = null,
  isUpdateMode: propIsUpdateMode = false,
  updateId: propUpdateId = null,
  onClose,
}) => {
  const [form, setForm] = useState({
    newTitle: "",
    newStatus: "Active",
    newDescription: "",
    variants: [],
    addOns: [],
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [syncProducts, setSyncProducts] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(propIsUpdateMode);
  const [updateId, setUpdateId] = useState(propUpdateId);

  // Initialize form if existing data is provided
    useEffect(() => {
    const initializeForm = async () => {
      if (existingData) {
        setForm({
          newTitle: existingData.newTitle || "",
          newStatus: existingData.newStatus || "Active",
          newDescription: existingData.newDescription || "",
          variants: existingData.variants || [],
          addOns: existingData.addOns || [],
          image: null,
        });
        setImagePreview(existingData.image || null);
        setIsUpdateMode(true);
        setUpdateId(existingData._id || propUpdateId);
      }
      else {
        setForm({
          newTitle: "",
          newStatus: "Active",
          newDescription: "",
          variants: [],
          addOns: [],
          image: null,
        });
        setImagePreview(null);
        setIsUpdateMode(false);
        setUpdateId(null)
      }
    }

    initializeForm();
  }, [existingData, propUpdateId, userId]); // Added userId to dependencies

  const handleChange = (e) => {
    const { id, value } = e.target;
    setForm((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  // ── Variant helpers ──────────────────────────────────────────────────────
  const addVariant = () => setForm(p => ({ ...p, variants: [...p.variants, { name: "", price: "" }] }));
  const updateVariant = (idx, field, val) => {
    const arr = [...form.variants]; arr[idx][field] = val;
    setForm(p => ({ ...p, variants: arr }));
  };
  const removeVariant = (idx) => setForm(p => ({ ...p, variants: p.variants.filter((_, i) => i !== idx) }));

  // ── Add-on helpers ───────────────────────────────────────────────────────
  const addAddon = () => setForm(p => ({ ...p, addOns: [...p.addOns, { name: "", price: "" }] }));
  const updateAddon = (idx, field, val) => {
    const arr = [...form.addOns]; arr[idx][field] = val;
    setForm(p => ({ ...p, addOns: arr }));
  };
  const removeAddon = (idx) => setForm(p => ({ ...p, addOns: p.addOns.filter((_, i) => i !== idx) }));

  const TABS = [
    { id: "general",  label: "General",          icon: "📋" },
    { id: "variants", label: `Variants (${form.variants.length})`, icon: "🍕" },
    { id: "addons",   label: `Add-ons (${form.addOns.length})`, icon: "🧀" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    
    // Validation
    if (!form.newTitle.trim()) {
      if (onSuccess) onSuccess("error", "Category name is required!");
      return;
    }


    setIsSubmitting(true);
    
    try {
      const data = new FormData();
      data.append("newTitle", form.newTitle);
      data.append("newStatus", form.newStatus);
      data.append("newDescription", form.newDescription);
      data.append("variants", JSON.stringify(form.variants));
      data.append("addOns", JSON.stringify(form.addOns));
      data.append("syncProducts", syncProducts);
      
      if (form.image) {
        data.append("image", form.image);
      } else if (isUpdateMode && existingData && existingData.image && imagePreview === null) {
        data.append("image", "");
      }

      if (isUpdateMode) {
        await api.put(`newcategories/${updateId}`, data);
      } else {
        await api.post(`newcategories/${userId}`, data);
      }
      
      // Reset form
      setForm({
        newTitle: "",
        newStatus: "Active",
        newDescription: "",
      });
      
 
      if (onSuccess) onSuccess(isUpdateMode ? "update" : "add");
      if (refreshCategories) refreshCategories();
      if (onClose) onClose();
      
    } catch (error) {
      console.error("Error saving category:", error);
      
      if (onSuccess) onSuccess("error", error.response?.data?.message || "Error adding category");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PopUpModal
      isOpen={true}
      onClose={onClose}
      title={isUpdateMode ? "Update Category" : "Add New Category"}
      size="xl"
    >

    <div className="flex border-b border-gray-200 mb-4">
      {TABS.map(tab => (
        <button
          key={tab.id}
          type="button"
          onClick={() => setActiveTab(tab.id)}
          className={`flex items-center gap-1.5 px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-200 ${
            activeTab === tab.id
              ? "border-teal-600 text-teal-700 bg-white"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
          }`}
        >
          <span>{tab.icon}</span>
          {tab.label}
        </button>
      ))}
    </div>

    <form onSubmit={handleSubmit} className="space-y-6">
      {activeTab === "general" && (
        <div className="space-y-6">
      <div>
        <label
          htmlFor="newTitle"
          className="block text-gray-700 font-medium mb-2"
        >
          Category Name <span className="text-red-500">*</span>
        </label>
        <input
          id="newTitle"
          type="text"
          className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent transition duration-200"
          value={form.newTitle}
          onChange={handleChange}
          required
          placeholder="e.g., Starters, Fast Food, Snacks, etc."
          disabled={isSubmitting}
          autoFocus
        />
        <div className="">
        <label
          htmlFor="newStatus"
          className="block text-gray-700 font-medium mb-2"
        >
          Status <span className="text-red-500">*</span>
        </label>
        <select
          id="newStatus"
          value={form.newStatus}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={isSubmitting}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        </div>
      </div>

      <div>
        <label
          htmlFor="newDescription"
          className="block text-gray-700 font-medium mb-2"
        >
          Description
        </label>
        <textarea
          id="newDescription"
          className="w-full border border-gray-300 rounded-lg p-3 resize-none focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-transparent transition duration-200"
          rows="4"
          value={form.newDescription}
          onChange={handleChange}
          placeholder="Enter category description (optional)"
          disabled={isSubmitting}
        ></textarea>
      </div>

      {/* Image Upload */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Category Image</label>
        <p className="text-xs text-gray-500 mb-3">Products in this category will use this image by default if they don't have one.</p>
        <div className="flex items-center gap-4">
          <label className={`flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed rounded-xl cursor-pointer transition ${
            imagePreview ? "border-teal-400" : "border-gray-300 hover:border-teal-400 bg-gray-50 hover:bg-teal-50"
          }`}>
            {imagePreview ? (
              <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-xl" />
            ) : (
              <div className="text-center p-3">
                <div className="text-3xl text-gray-400">📷</div>
                <p className="text-xs text-gray-400 mt-1">Click to upload</p>
              </div>
            )}
            <input type="file" accept="image/*" className="hidden" disabled={isSubmitting}
              onChange={e => {
                const file = e.target.files[0];
                if (file) { setForm(p => ({ ...p, image: file })); setImagePreview(URL.createObjectURL(file)); }
              }} />
          </label>
          {imagePreview && (
            <button type="button" onClick={() => { setForm(p => ({ ...p, image: null })); setImagePreview(null); }}
              className="text-xs text-red-500 hover:text-red-700">Remove</button>
          )}
        </div>
      </div>
      
      {/* Sync Products Toggle (Only in Update Mode) */}
      {isUpdateMode && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mt-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <div className="relative flex items-center pt-0.5">
              <input type="checkbox" className="sr-only" checked={syncProducts} disabled={isSubmitting}
                onChange={(e) => setSyncProducts(e.target.checked)} />
              <div className={`block w-10 h-6 rounded-full transition-colors ${syncProducts ? 'bg-orange-500' : 'bg-gray-300'}`}></div>
              <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${syncProducts ? 'transform translate-x-4' : ''}`}></div>
            </div>
            <div>
              <p className="text-sm font-semibold text-orange-800">Force Sync to Existing Products</p>
              <p className="text-xs text-orange-700 mt-1">If enabled, ALL existing products in this category will be updated to perfectly match this category's Image, Variants, and Add-ons. This overrides any custom settings they might have.</p>
            </div>
          </label>
        </div>
      )}
      </div>
      )}

      {activeTab === "variants" && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex justify-between items-start gap-3">
            <div>
              <p className="text-sm font-semibold text-blue-800 mb-0.5">🍕 Default Variants</p>
              <p className="text-xs text-blue-600">Variants added here will automatically apply to all new products in this category.</p>
            </div>
            <button type="button" onClick={addVariant}
              className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2 rounded-xl shadow-sm transition">
              + Add Variant
            </button>
          </div>

          {form.variants.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-gray-400">
              <span className="text-5xl mb-3">🍕</span>
              <p className="text-sm">No default variants yet.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[40vh] overflow-y-auto px-1">
              {form.variants.map((v, idx) => (
                <div key={idx} className="flex gap-3 items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 font-medium block mb-1">Name</label>
                    <input type="text" list="predefined-variants" placeholder="e.g. Medium"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={v.name} onChange={e => updateVariant(idx, "name", e.target.value)} />
                    <datalist id="predefined-variants">
                      <option value="Half" />
                      <option value="Full" />
                      <option value="Quarter" />
                      <option value="Small" />
                      <option value="Medium" />
                      <option value="Large" />
                      <option value="Regular" />
                      <option value="Jumbo" />
                      <option value="Single" />
                      <option value="Double" />
                    </datalist>
                  </div>
                  <div className="w-28">
                    <label className="text-xs text-gray-500 font-medium block mb-1">Price (₹)</label>
                    <input type="number" placeholder="0.00" min="0" step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={v.price} onChange={e => updateVariant(idx, "price", e.target.value)} />
                  </div>
                  <button type="button" onClick={() => removeVariant(idx)}
                    className="mt-5 text-red-400 hover:text-red-600 text-xl leading-none">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "addons" && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex justify-between items-start gap-3">
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-0.5">🧀 Default Add-ons</p>
              <p className="text-xs text-amber-700">Add-ons added here will automatically apply to all new products in this category.</p>
            </div>
            <button type="button" onClick={addAddon}
              className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-4 py-2 rounded-xl shadow-sm transition">
              + Add Add-on
            </button>
          </div>

          {form.addOns.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-gray-400">
              <span className="text-5xl mb-3">🧀</span>
              <p className="text-sm">No default add-ons yet.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[40vh] overflow-y-auto px-1">
              {form.addOns.map((a, idx) => (
                <div key={idx} className="flex gap-3 items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 font-medium block mb-1">Name</label>
                    <input type="text" list="predefined-addons" placeholder="e.g. Extra Cheese"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      value={a.name} onChange={e => updateAddon(idx, "name", e.target.value)} />
                    <datalist id="predefined-addons">
                      <option value="Extra Cheese" />
                      <option value="Extra Butter" />
                      <option value="Extra Mayo" />
                      <option value="Extra Toppings" />
                      <option value="Ketchup" />
                    </datalist>
                  </div>
                  <div className="w-28">
                    <label className="text-xs text-gray-500 font-medium block mb-1">Price (₹)</label>
                    <input type="number" placeholder="0.00" min="0" step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      value={a.price} onChange={e => updateAddon(idx, "price", e.target.value)} />
                  </div>
                  <button type="button" onClick={() => removeAddon(idx)}
                    className="mt-5 text-red-400 hover:text-red-600 text-xl leading-none">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition duration-200 font-medium focus:outline-none focus:ring-1 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : isUpdateMode ? (
            "Update Category"
          ) : (
            "Add Category"
          )}
        </button>
      </div>
    </form>

    </PopUpModal>
  );
};

export default AddOneCategory;